import React from 'react';
import {
  Spacing, Portal, useModal, useNotifications, FormSkeleton, Message, Action, FormHandlers, Fieldset,
  Dropdown,
} from '@doveit/bricks';
import { useNavigate } from 'react-router-dom';
import { PropertyPreview } from '../../../providers/api/dtos/property';
import PropertyAutoComplete from '../../../containers/property-autocomplete/PropertyAutoComplete';
import { Status, IntentStatus, IntentSource } from '../../../domain/types';
import CreateIntentForm, { CreateIntentFormModel } from '../../components/create-intent-form/CreateIntentForm';
import { Contact } from '../../../providers/api/dtos/contact';
import { createIntent, getIntents } from '../../../providers/api/intent/intentProvider';
import { Intent } from '../../../providers/api/dtos/intent';
import SimpleModal from '../../../components/simple-modal/SimpleModal';
import { SortOrder } from '../../../providers/pagination';
import useContact from '../../../contact/hooks/use-contact/useContact';
import createOrUpdateContact from '../../../contact/utils/createOrUpdateContact';
import { AppointmentAvailability, PropertySearchItem } from '../../../providers/api/dtos';
import useRBAC from '../../../hooks/use-rbac/useRBAC';
import usePropertyPreview from '../../../property/hooks/use-property-preview/usePropertyPreview';
import useMarkIntentAsSeen from '../../../hooks/use-mark-intent-as-seen/useMarkIntentAsSeen';
import { useAgentByPropertyId } from '../../../hooks/use-agent/useAgent';
import SelectContact from '../../../contact/containers/select-contact/SelectContact';
import validationSchema from '../../../contact/components/contact-form/ContactForm.schema';
import { ContactStatus } from '../../../contact/containers/select-contact/types';
import ViewPropertyPreview from '../../../property/containers/view-property-preview/ViewPropertyPreview';

export const SAVE_INTENT_SUCCESS_MESSAGE = 'Interesse d\'acquisto salvato. <a href="__LINK__" target="_blank">Visualizza</a>';
export const SAVE_INTENT_ERROR_MESSAGE = 'Non è stato possibile salvare l\'interesse d\'acquisto';
export const LOAD_PREFILLED_DATA_ERROR_MESSAGE = 'Non è stato possibile caricare i dati necessari per creare l\'interesse';
export const DUPLICATED_INTENT_WARNING_MESSAGE = 'Non è stato possibile creare un nuovo interesse perché già esistente.';
export const DUPLICATED_INTENT_WARNING_MESSAGE_WITH_UPDATED_CONTACT = 'Non è stato possibile creare un nuovo interesse perché già esistente. Aggiorna i dati del contatto per mantenere le modifiche.';
export const SAVE_CONTACT_SUCCESS_MESSAGE = 'Dati del contatto aggiornati con successo.';
export const SAVE_CONTACT_ERROR_MESSAGE = 'Non è stato possibile aggiornare i dati del contatto.';

export interface CreateIntentProps {
  propertyId?: number,
  contactId?: string,
  source?: IntentSource,
  formRef?: React.MutableRefObject<FormHandlers>,
  onStartSaving?: VoidFunction,
  onCreateIntentSuccess?: (intent: Intent) => void,
  onUpsertContactSuccess?: (contact: Contact) => void,
  onCreateIntentError?: (error: Error) => void,
  onUpsertContactError?: (error: Error) => void,
  onFormStatusChanged?: (status: boolean) => void,
}

const CreateIntent: React.FC<CreateIntentProps> = ({
  propertyId,
  contactId,
  source,
  formRef,
  onStartSaving,
  onCreateIntentSuccess,
  onUpsertContactSuccess,
  onCreateIntentError,
  onUpsertContactError,
  onFormStatusChanged,
}) => {
  const navigate = useNavigate();
  const { data: agent } = useAgentByPropertyId(propertyId);
  const [selectedProperty, setSelectedProperty] = React.useState<PropertySearchItem | PropertyPreview | null>(null);
  const { markAsSeen } = useMarkIntentAsSeen(propertyId ? agent?.id : (selectedProperty as PropertySearchItem)?.agentId);
  const [selectedContact, setSelectedContact] = React.useState<Contact | undefined>();
  const [overwriteValues, setOverwriteValues] = React.useState<Partial<Contact> | undefined>();
  const [isSaving, setIsSaving] = React.useState(false);
  const [hasDuplicateContact, setHasDuplicateContact] = React.useState(false);
  const { addSuccess, addError } = useNotifications();
  const {
    user, userIsAdmin, userIsAgent, userIsCallCenter, mainUserRole,
  } = useRBAC();
  const existingIntentModal = useModal();
  const [existingIntent, setExistingIntent] = React.useState<Intent | undefined>();
  const {
    data: requestedProperty,
    isLoading: isRequestedPropertyLoading,
    error: requestedPropertyError,
  } = usePropertyPreview(propertyId);
  const {
    data: requestedContact,
    isLoading: isRequestedContactLoading,
    error: requestedContactError,
  } = useContact(contactId);

  React.useEffect(() => onFormStatusChanged?.(hasDuplicateContact), [hasDuplicateContact, onFormStatusChanged]);

  React.useEffect(() => {
    if (requestedProperty) {
      setSelectedProperty(requestedProperty);
    }
  }, [requestedProperty, requestedPropertyError]);

  React.useEffect(() => {
    if (requestedContact) {
      setSelectedContact(requestedContact);
    }
  }, [requestedContact, requestedContactError]);

  const warningMessage = React.useMemo(() => {
    if ((propertyId && selectedProperty) || (contactId && selectedContact)) {
      let message = 'Stai inserendo un interesse';

      if (selectedContact) {
        message += ` del contatto <strong>${selectedContact.name || selectedContact.phoneNumber || selectedContact.email}</strong>`;
      }

      if (selectedProperty) {
        message += ` per l'immobile <strong>${selectedProperty.referenceId}</strong>`;
      }

      return (
        <Message
          type="warning"
          aria-label="Messaggio utente dati prepopolati"
          message={message}
        />
      );
    }

    return undefined;
  }, [contactId, propertyId, selectedContact, selectedProperty]);

  const onPropertyAutoCompleteChange = React.useCallback(
    () => setSelectedProperty(null),
    [],
  );

  const onContactChange = React.useCallback((status: ContactStatus, contactData?: Contact, search?: Partial<Contact>) => {
    setSelectedContact(contactData);
    setOverwriteValues(search);
    setHasDuplicateContact(status === 'DUPLICATE');
  }, []);

  const onPropertySelected = React.useCallback(
    (property: PropertyPreview | PropertySearchItem | null) => setSelectedProperty(property),
    [],
  );

  const viewExistingIntent = React.useCallback(
    () => navigate(`/intents/${existingIntent!.id}`),
    [navigate, existingIntent],
  );

  const abortPropertySelection = React.useCallback(
    () => setSelectedProperty(null),
    [],
  );

  const updateContactOnly = React.useCallback(async () => {
    setIsSaving(true);
    onStartSaving?.();

    try {
      const upsertedContact = await createOrUpdateContact(overwriteValues, selectedContact);
      addSuccess(SAVE_CONTACT_SUCCESS_MESSAGE);
      setIsSaving(false);
      onUpsertContactSuccess?.(upsertedContact);
      existingIntentModal.close();
    } catch (error) {
      addError(SAVE_CONTACT_ERROR_MESSAGE);
      setIsSaving(false);

      if (error instanceof Error) {
        onUpsertContactError?.(error);
      }
    }
  }, [addError, addSuccess, existingIntentModal, onStartSaving, onUpsertContactError, onUpsertContactSuccess, overwriteValues, selectedContact]);

  const onSubmit = React.useCallback(async (values: CreateIntentFormModel) => {
    setIsSaving(true);
    onStartSaving?.();

    try {
      setExistingIntent(undefined);
      let contact = selectedContact;

      const searchResults = await getIntents(
        {
          contactId: contact && contact.id ? contact.id : undefined,
          propertyId: selectedProperty!.id!,
          excludeStatus: IntentStatus.KO_DUPLICATE,
        },
        {
          page: 0,
          sort: {
            createdAt: SortOrder.ASC,
          },
        },
      );

      if (contact?.id && searchResults.content.length > 0) {
        setExistingIntent(searchResults.content[0]);
        existingIntentModal.open();
        onCreateIntentError?.(new Error('Duplicate intent found'));
      } else {
        contact = await createOrUpdateContact(overwriteValues, selectedContact);

        const payload: Intent = {
          contactId: contact!.id!,
          propertyId: selectedProperty!.id!,
          source: values.source!,
          status: IntentStatus.IN_PROGRESS,
          appointmentAvailabilities: Object.keys(values.availabilities).filter(
            (availability) => values.availabilities[availability as AppointmentAvailability],
          ) as AppointmentAvailability[],
          notes: values.notes
            ? [{
              author: user!.name,
              role: mainUserRole!,
              date: new Date().toISOString(),
              text: values.notes,
            }]
            : [],
        };

        let intent;
        intent = await createIntent(payload);
        intent = await markAsSeen(intent);

        addSuccess(SAVE_INTENT_SUCCESS_MESSAGE.replace('__LINK__', `/intents/${intent.id}`), 5000);
        onCreateIntentSuccess?.(intent);
      }
    } catch (error) {
      addError(SAVE_INTENT_ERROR_MESSAGE);

      if (error instanceof Error) {
        onCreateIntentError?.(error);
      }
    }

    setIsSaving(false);
  }, [onStartSaving, selectedContact, selectedProperty, existingIntentModal, overwriteValues, user, mainUserRole, markAsSeen, addSuccess, onCreateIntentSuccess, addError, onCreateIntentError]);

  if (requestedPropertyError || requestedContactError) {
    return (
      <Message
        type="critical"
        message={LOAD_PREFILLED_DATA_ERROR_MESSAGE}
      />
    );
  }

  if ((propertyId && isRequestedPropertyLoading) || (contactId && isRequestedContactLoading)) {
    return <FormSkeleton aria-label="form-skeleton" />;
  }

  return (
    <div>
      {warningMessage && (
        <Spacing margin={[0, 0, 200]}>
          {warningMessage}
        </Spacing>
      )}
      {!propertyId && (
        <Fieldset legend="Dati immobile">
          {!selectedProperty && (
            <PropertyAutoComplete
              dataRef="property-autocomplete"
              aria-label="Seleziona un immobile"
              placeholder="Cerca immobile"
              status={[Status.LIVE, Status.DRAFT, Status.ANTEPRIMA]}
              onChange={onPropertyAutoCompleteChange}
              onSuggestionSelected={onPropertySelected}
              maxSuggestions={10}
            />
          )}
          {selectedProperty && (
            <ViewPropertyPreview
              aria-label="Anteprima dell'immobile selezionato"
              property={selectedProperty}
              secondaryActions={[
                <Dropdown.Option
                  label="Visualizza"
                  aria-label="Visualizza la pagina dell'immobile"
                  href={`/properties/${selectedProperty.id}`}
                  target="_blank"
                />, !propertyId && (
                  <Dropdown.Option
                    label="Rimuovi"
                    aria-label="Annulla la selezione dell'immobile"
                    onClick={abortPropertySelection}
                  />
                )]}
            />
          )}
        </Fieldset>
      )}
      {!contactId && (
        <Spacing margin={[300, 0, 0]}>
          <Fieldset legend="Dati contatto">
            <SelectContact
              onContactChange={onContactChange}
              initialContact={requestedContact}
              showClearContact={!contactId}
              editContactSchema={validationSchema}
            />
          </Fieldset>
        </Spacing>
      )}
      <Spacing margin={[300, 0, 0]}>
        <Fieldset legend="Dati interesse">
          <CreateIntentForm
            initialValues={source && { source }}
            onSubmit={onSubmit}
            loading={!user || isSaving}
            showAvailabilities={userIsAdmin || userIsAgent || userIsCallCenter}
            hideSource={!!source}
            disabled={!selectedProperty || (!selectedContact && !overwriteValues)}
            innerRef={formRef}
          />
        </Fieldset>
      </Spacing>
      <Portal>
        <SimpleModal
          {...existingIntentModal}
          title="Interesse d'acquisto duplicato"
          aria-label="duplicated-intent-modal"
          loading={isSaving}
          footer={(
            <>
              {overwriteValues && (
                <Action
                  aria-label="updated-contact-data"
                  label="Aggiorna contatto"
                  emphasis="high"
                  onClick={updateContactOnly}
                  loading={isSaving}
                />
              )}
              <Action
                dataRef="view-existing-intent"
                label="Visualizza interesse"
                emphasis="high"
                onClick={viewExistingIntent}
                disabled={isSaving}
              />
            </>
          )}
        >
          <Message
            type="warning"
            message={overwriteValues
              ? DUPLICATED_INTENT_WARNING_MESSAGE_WITH_UPDATED_CONTACT
              : DUPLICATED_INTENT_WARNING_MESSAGE}
          />
        </SimpleModal>
      </Portal>
    </div>
  );
};

export default CreateIntent;
